import { useState } from "react";
import { fmtMoney } from "../../lib/format";

function gcd(a, b) { return b ? gcd(b, a % b) : a; }

// Best simple fraction for x (0..∞), denominator capped.
export function approxFraction(x, maxDen = 999) {
  if (!isFinite(x) || x <= 0) return null;
  let bestN = 1, bestD = 1, bestErr = Infinity;
  for (let d = 1; d <= maxDen; d++) {
    const n = Math.round(x * d);
    if (n === 0) continue;
    const err = Math.abs(x - n / d);
    if (err < bestErr - 1e-12) {
      bestN = n; bestD = d; bestErr = err;
      if (err < 1e-9) break;
    }
  }
  const g = gcd(bestN, bestD) || 1;
  return [bestN / g, bestD / g];
}

export function fracLabel(x, maxDen = 999) {
  const f = approxFraction(x, maxDen);
  return f ? `${f[0]}/${f[1]}` : null;
}

// Decimal ratio label, e.g. 0.5 · trims trailing zeros, max 3 dp.
export function decRatio(x) {
  if (!isFinite(x) || x <= 0) return null;
  return String(Math.round(x * 1000) / 1000);
}

// Ratio-style month cell for the forecast scope grid.
// Accepts "5/10" (fraction of contract value), "0.5" (decimal fraction),
// or a raw dollar amount — always displays as a stacked fraction over the
// computed dollar amount. Stores dollars in revenue + the typed ratio label.
// When `negativeDefault` is true (sub-consultants row), a bare number is
// stored negated; "+" forces positive; "-" behaves as it always has.
// Ratio / decimal-fraction paths follow the sign of the contract value (cv).
export default function RatioCell({ cv, value, ratio, onChange, testid, pastMonth = false, readOnly = false, negativeDefault = false }) {
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState("");

  const commit = () => {
    const raw = String(draft).trim();
    setFocused(false);
    if (!raw) { onChange(0, null); return; }
    // Fraction path — dollar sign follows cv naturally (cv * n/d).
    const fracMatch = raw.match(/^([+-]?\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)$/);
    if (fracMatch && cv !== 0) {
      const n = Number(fracMatch[1]), d = Number(fracMatch[2]);
      if (d > 0) {
        const amt = Math.round(cv * (n / d) * 100) / 100;
        onChange(amt, `${n}/${d}`);
        return;
      }
    }
    // Sign detection BEFORE stripping.
    const hasPlus  = /^\+/.test(raw);
    const hasMinus = /^-/.test(raw);
    const magnitude = Number(raw.replace(/[^0-9.]/g, "")) || 0;
    if (magnitude <= 0) { onChange(0, null); return; }
    // Decimal fraction of cv (e.g. 0.5) — dollars follow the SIGN OF cv, so a
    // bare 0.25 on a sub-consultant row (cv negative) is a deduction. An
    // explicit "+" (or "-" on a positive cv) flips it.
    if (Math.abs(cv) > 0 && magnitude < 1 && !raw.includes("$")) {
      const f = approxFraction(magnitude);
      const flip = (hasMinus && cv > 0) || (hasPlus && cv < 0) ? -1 : 1;
      const finalDollars = Math.round(cv * magnitude * flip * 100) / 100;
      onChange(finalDollars, f ? `${f[0]}/${f[1]}` : null);
      return;
    }
    // Raw dollars — apply the sign rule.
    const signed = hasMinus ? -magnitude : (hasPlus ? magnitude : (negativeDefault ? -magnitude : magnitude));
    const f = Math.abs(cv) > 0 ? approxFraction(Math.abs(signed) / Math.abs(cv)) : null;
    onChange(signed, f ? `${f[0]}/${f[1]}` : null);
  };

  if (focused && !readOnly) {
    return (
      <input
        autoFocus
        type="text"
        inputMode="decimal"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
        placeholder={Math.abs(cv) > 0 ? "5/10 · 0.5 · $" : "$"}
        className="w-full px-2 py-2 bg-white font-mono-num text-xs text-right outline-none border-0 placeholder:text-[var(--rule-strong)]"
        data-testid={testid}
      />
    );
  }

  const shownRatio = Math.abs(cv) > 0 && value !== 0 ? decRatio(Math.abs(value) / Math.abs(cv)) : null;
  const pastCls = pastMonth ? "bg-[var(--paper-soft)]/70" : "";

  if (readOnly) {
    return (
      <div
        className={`w-full px-2 py-2 text-right font-mono-num min-h-[34px] ${pastCls} cursor-default select-text`}
        data-testid={testid}
        title={shownRatio ? `${shownRatio} of contract value · past month, locked` : "Past month — locked"}
      >
        {value ? (
          <span className="block text-xs leading-tight whitespace-nowrap text-[var(--muted-fg)]">
            {shownRatio && <span className="opacity-70">{shownRatio} · </span>}
            {value < 0 ? `−${fmtMoney(Math.abs(value))}` : fmtMoney(value)}
          </span>
        ) : (
          <span className="text-[var(--rule-strong)] text-xs">—</span>
        )}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => {
        setDraft(value ? String(value) : "");
        setFocused(true);
      }}
      onFocus={() => {
        setDraft(value ? String(value) : "");
        setFocused(true);
      }}
      className={`w-full px-2 py-2 text-right font-mono-num hover:bg-[var(--paper-soft)]/40 cursor-text min-h-[34px] ${pastCls}`}
      data-testid={testid}
      title={shownRatio ? `${shownRatio} of contract value` : undefined}
    >
      {value ? (
        <span className="block text-xs leading-tight whitespace-nowrap">
          {shownRatio && <span className="text-[var(--soft-fg)]">{shownRatio} · </span>}
          {value < 0 ? `−${fmtMoney(Math.abs(value))}` : fmtMoney(value)}
        </span>
      ) : (
        <span className="text-[var(--rule-strong)] text-xs">—</span>
      )}
    </button>
  );
}
